"use client";

import { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import type { IFaqItem } from "@/lib/sanity/types";
import { cn } from "@/lib/utils";

interface IFaqAccordionProps {
  items: IFaqItem[];
  defaultOpenIndex?: number;
  className?: string;
}

function renderFaqAnswer(answer: string): React.ReactNode {
  const lines = answer
    .replace(/\r/g, "")
    .split("\n")
    .map((l) => l.trim());

  const bulletRegex = /^(?:•|●|-)\s+/;
  const orderedRegex = /^(\d+)\.\s+/;

  const nodes: React.ReactNode[] = [];
  let paragraphBuffer: string[] = [];
  let bulletBuffer: string[] = [];
  let orderedBuffer: string[] = [];

  const flushParagraph = () => {
    if (paragraphBuffer.length === 0) return;
    nodes.push(
      <p key={`p-${nodes.length}`} className="leading-7 text-foreground/90">
        {paragraphBuffer.join(" ")}
      </p>
    );
    paragraphBuffer = [];
  };

  const flushBullets = () => {
    if (bulletBuffer.length === 0) return;
    nodes.push(
      <ul
        key={`ul-${nodes.length}`}
        className="list-disc space-y-1 pl-5 text-foreground/90"
      >
        {bulletBuffer.map((t) => (
          <li key={t} className="leading-7">
            {t}
          </li>
        ))}
      </ul>
    );
    bulletBuffer = [];
  };

  const flushOrdered = () => {
    if (orderedBuffer.length === 0) return;
    nodes.push(
      <ol
        key={`ol-${nodes.length}`}
        className="list-decimal space-y-1 pl-5 text-foreground/90"
      >
        {orderedBuffer.map((t) => (
          <li key={t} className="leading-7">
            {t}
          </li>
        ))}
      </ol>
    );
    orderedBuffer = [];
  };

  for (const line of lines) {
    if (!line) {
      flushParagraph();
      flushBullets();
      flushOrdered();
      continue;
    }

    const bulletMatch = line.match(bulletRegex);
    if (bulletMatch) {
      flushParagraph();
      flushOrdered();
      bulletBuffer.push(line.replace(bulletRegex, "").trim());
      continue;
    }

    const orderedMatch = line.match(orderedRegex);
    if (orderedMatch) {
      flushParagraph();
      flushBullets();
      orderedBuffer.push(line.replace(orderedRegex, "").trim());
      continue;
    }

    // Default to paragraph text.
    flushBullets();
    flushOrdered();
    paragraphBuffer.push(line);
  }

  flushParagraph();
  flushBullets();
  flushOrdered();

  return nodes;
}

export function FaqAccordion({
  items,
  defaultOpenIndex = 0,
  className,
}: IFaqAccordionProps): React.ReactElement {
  const [openIndex, setOpenIndex] = useState<number | null>(
    items[defaultOpenIndex] != null ? defaultOpenIndex : null
  );

  const safeItems = useMemo(() => items ?? [], [items]);

  return (
    <div className={cn("space-y-3", className)}>
      {safeItems.map((item, index) => {
        const isOpen = openIndex === index;
        const buttonId = `faq-btn-${item._id}`;
        const panelId = `faq-panel-${item._id}`;

        return (
          <div
            key={item._id}
            className={cn(
              "overflow-hidden rounded-2xl border bg-card transition-colors",
              isOpen ? "border-primary/45" : "border-border"
            )}
          >
            <button
              id={buttonId}
              type="button"
              onClick={() => setOpenIndex((prev) => (prev === index ? null : index))}
              aria-expanded={isOpen}
              aria-controls={panelId}
              className="flex w-full items-start justify-between gap-4 px-6 py-5 text-left"
            >
              <span className="font-sans text-[17px] font-semibold leading-normal text-foreground">
                {item.question}
              </span>
              <span
                className={cn(
                  "mt-0.5 grid h-7 w-7 flex-none place-items-center rounded-full bg-primary/10 text-primary transition-transform duration-200",
                  isOpen && "rotate-45"
                )}
                aria-hidden="true"
              >
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.4"
                  strokeLinecap="round"
                >
                  <path d="M8 2v12M2 8h12" />
                </svg>
              </span>
            </button>

            <AnimatePresence initial={false}>
              {isOpen && (
                <motion.div
                  id={panelId}
                  role="region"
                  aria-labelledby={buttonId}
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <div className="border-t border-border px-6 py-5">
                    <div className="space-y-2">{renderFaqAnswer(item.answer)}</div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </div>
  );
}

